export const INDIAN_TIMEZONE = 'Asia/Kolkata'

/**
 * Formats a date/time string or Date object in Indian Standard Time (IST),
 * e.g., "19 Oct 2026, 10:50 am".
 */
export function formatIndianDateTime(
  value?: Date | string | number | null,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!value) return ''
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: INDIAN_TIMEZONE,
    ...options,
  })
}

/**
 * Converts a datetime-local input string ("YYYY-MM-DDTHH:mm")
 * into an absolute UTC ISO string representing that wall-clock time in Indian Standard Time (IST, UTC+05:30).
 */
export function toIndianIsoString(datetimeLocalStr?: string | null): string {
  if (!datetimeLocalStr) return ''
  // If it's already an ISO string with Z or timezone offset, parse directly
  if (/[zZ]|[\+\-]\d{2}:?\d{2}$/.test(datetimeLocalStr)) {
    const d = new Date(datetimeLocalStr)
    return isNaN(d.getTime()) ? '' : d.toISOString()
  }
  // Otherwise treat wall-clock date-time as IST (+05:30)
  const normalized = datetimeLocalStr.includes('T') ? datetimeLocalStr : datetimeLocalStr.replace(' ', 'T')
  const full = normalized.length === 16 ? `${normalized}:00+05:30` : `${normalized}+05:30`
  const d = new Date(full)
  return isNaN(d.getTime()) ? new Date(datetimeLocalStr).toISOString() : d.toISOString()
}

/**
 * Converts a UTC ISO string or Date into a string suitable for <input type="datetime-local" />
 * ("YYYY-MM-DDTHH:mm") representing that moment in Indian Standard Time (IST).
 */
export function toIndianDatetimeLocal(dateOrIso?: Date | string | null): string {
  if (!dateOrIso) return ''
  const d = dateOrIso instanceof Date ? dateOrIso : new Date(dateOrIso)
  if (Number.isNaN(d.getTime())) return ''
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: INDIAN_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  // en-CA formats as 'YYYY-MM-DD, HH:mm'
  return formatter.format(d).replace(', ', 'T')
}
